import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Home, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Surfaces existing accommodation_allocations data for a participant.
 * Read-only here — all writes still happen via the Houses module.
 * Splits into "Current" (no move_out_date) and "History" (has move_out_date).
 */
export function CurrentResidenceTab({ participantId, mode = 'current' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/accommodation/by-participant/${participantId}`)
      .then(r => { if (!cancelled) setItems(r.data?.data || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const filtered = items.filter(a => mode === 'current'
    ? !a.move_out_date
    : !!a.move_out_date);

  const title = mode === 'current' ? 'Current Residence' : 'Tenancy History';
  const emptyMsg = mode === 'current'
    ? 'No active accommodation allocation. Assign one from the Houses module.'
    : 'No past tenancies recorded.';

  return (
    <div className="space-y-4" data-testid={`accommodation-${mode}-tab`}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Home className="h-4 w-4" /> {title}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Sourced from <code className="text-xs bg-muted px-1 rounded">/accommodation/by-participant</code> — Houses module is the source of truth.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>House</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Move-in</TableHead>
                <TableHead>Move-out</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{emptyMsg}</TableCell></TableRow>
              ) : filtered.map(a => (
                <TableRow key={a.id || a._id || `${a.house_id}-${a.move_in_date}`} data-testid={`residence-row-${a.id}`}>
                  <TableCell className="font-medium">{a.house_name || a.house_id || '—'}</TableCell>
                  <TableCell>{a.room_name || a.room_id || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{a.move_in_date || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {a.move_out_date ? a.move_out_date : <Badge variant="outline" className="text-[10px]">Active</Badge>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={a.notes || ''}>{a.notes || '—'}</TableCell>
                  <TableCell>
                    {a.house_id && (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"
                        onClick={() => navigate(`/houses/${a.house_id}`)}
                        data-testid={`open-house-${a.house_id}`}
                      >
                        <ExternalLink className="h-3 w-3" /> House
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default CurrentResidenceTab;
